import {
  Box,
  Button,
  Card,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  ActionIcon,
  SimpleGrid,
  Menu,
  useMantineColorScheme,
  useMantineTheme,
  ScrollArea,
  Paper,
  Highlight,
  Loader,
} from "@mantine/core";
import { useState, useRef, useEffect } from "react";
import {
  ArrowsDownUpIcon,
  CaretDownIcon,
  DotsThreeVerticalIcon,
  PaperPlaneRightIcon,
  QuestionIcon,
  StarFourIcon,
  MagnifyingGlassIcon,
  ClockCounterClockwise,
} from "@phosphor-icons/react";
import { useQuestionSuggestions } from "@/api";
import { useRecentSearches } from "@/api";
import { useDeleteRecentSearch } from "@/api";
import { useTrackSelection } from "@/api";
import { useDebouncedValue } from "@mantine/hooks";


export default function Shortcut() {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedQuestionForRelated, setSelectedQuestionForRelated] = useState<string>("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 500);

  const { data: questionSuggestions, isLoading: isLoadingSuggestions } = useQuestionSuggestions(
    { search: searchQuery },
    {
      query: {
        enabled: debouncedSearchQuery.length > 4,
        queryKey: ["question-suggestions", debouncedSearchQuery],
      },
    }
  );

  const { data: exampleQuestions = [] } = useQuestionSuggestions(
    selectedQuestionForRelated.length > 0 
      ? { search: selectedQuestionForRelated } 
      : debouncedSearchQuery.length > 0 
        ? { search: debouncedSearchQuery } 
        : {},
    {
      query: {
        queryKey: ["question-suggestions-examples", selectedQuestionForRelated || debouncedSearchQuery],
      },
    }
  );

  const { data: recentSearchesData, refetch: refetchRecentSearches, isLoading: isLoadingRecentSearches, error: recentSearchesError } = useRecentSearches({
    query: {
      queryKey: ["recent-searches"],
    },
  });

  const deleteRecentSearchMutation = useDeleteRecentSearch();
  const trackSelectionMutation = useTrackSelection();

  const recentSearches = recentSearchesData?.recent_searches || [];

  // Search suggestions state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  
  const [expandedExampleId, setExpandedExampleId] = useState<number | null>(null);

  // Health-related search suggestions database
  const healthSuggestions = [
    // Common health questions
    "How to manage diabetes",
    "Blood pressure medication side effects",
    "Heart disease prevention",
    "Mental health support",
    "Weight loss strategies",
    "Exercise for seniors",
    "Nutrition for diabetes",
    "Sleep disorders treatment",
    "Chronic pain management",
    "Allergy symptoms and treatment",

    // Medical conditions
    "Hypertension management",
    "Diabetes type 2 treatment",
    "Asthma symptoms",
    "Arthritis pain relief",
    "Depression symptoms",
    "Anxiety treatment",
    "High cholesterol diet",
    "Thyroid problems",
    "Migraine prevention",
    "Back pain exercises",

    // Medications
    "Metformin side effects",
    "Blood pressure medication",
    "Cholesterol lowering drugs",
    "Antidepressant medication",
    "Pain relief options",
    "Vitamin D deficiency",
    "Iron supplements",
    "Probiotics benefits",
    "Omega 3 supplements",
    "Calcium supplements",

    // Lifestyle and prevention
    "Healthy eating habits",
    "Exercise routine for beginners",
    "Stress management techniques",
    "Quit smoking methods",
    "Alcohol consumption guidelines",
    "Regular health checkups",
    "Vaccination schedule",
    "Sun protection tips",
    "Hydration benefits",
    "Meditation for health",

    // Emergency and urgent care
    "When to call 911",
    "Heart attack symptoms",
    "Stroke warning signs",
    "Severe allergic reaction",
    "High fever treatment",
    "Chest pain evaluation",
    "Difficulty breathing",
    "Severe headache",
    "Abdominal pain causes",
    "Emergency first aid",
  ];

  // Recent searches (simulated)
  // const recentSearches = [
  //   "How to manage diabetes",
  //   "Blood pressure medication",
  //   "Mental health support",
  //   "Exercise for seniors",
  //   "Sleep disorders treatment",
  // ];

  // Trending searches (simulated)
  // const trendingSearches = [
  //   "Winter flu prevention",
  //   "Holiday stress management",
  //   "New Year health goals",
  //   "Vitamin D deficiency",
  //   "Heart health tips",
  // ];

  // Filter suggestions based on search query
  const filteredSuggestions = healthSuggestions
    .filter((suggestion) =>
      suggestion.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .slice(0, 8); // Limit to 8 suggestions

  // Handle input change
  const handleInputChange = (value: string) => {
    setSearchQuery(value);
    setShowSuggestions(value.length > 0);
    setSelectedSuggestionIndex(-1);
    setSelectedQuestionForRelated("");
    setExpandedExampleId(null);
  };

  const handleSuggestionClick = (suggestion: string | any) => {
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    setExpandedExampleId(null);
    
    if (suggestion && typeof suggestion === 'object' && suggestion.question) {
      setSelectedQuestionForRelated(suggestion.question);
    } else if (typeof suggestion === 'string') {
      setSelectedQuestionForRelated(suggestion);
    }
    
    if (suggestion && typeof suggestion === 'object' && suggestion.id) {
      trackSelectionMutation.mutate({
        data: {
          search_keyword: searchQuery,
          question_id: suggestion.id
        },
      }, {
        onSuccess: () => {
          refetchRecentSearches();
        }
      });
    }
    
  };

  const handleExampleQuestionClick = (question: any) => {
    if (expandedExampleId === question.id) {
      setExpandedExampleId(null);
    } else {
      setExpandedExampleId(question.id);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!showSuggestions) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        event.preventDefault();
        if (
          selectedSuggestionIndex >= 0 &&
          filteredSuggestions[selectedSuggestionIndex]
        ) {
          handleSuggestionClick(filteredSuggestions[selectedSuggestionIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        setExpandedExampleId(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Simulate loading for search suggestions
  // useEffect(() => {
  //   if (searchQuery.length > 0) {
  //     setIsLoading(true);
  //     const timer = setTimeout(() => {
  //       setIsLoading(false);
  //     }, 300);
  //     return () => clearTimeout(timer);
  //   }
  // }, [searchQuery]);

  return (
    <Group align="flex-start" gap="lg">
      {/* Left Panel - New Chat & Recent Chats */}
      <Card withBorder radius="md" p="md" w={300}>
        <Stack gap="md">
          <Button
            leftSection={<StarFourIcon weight="fill" size={20} />}
            variant="filled"
            radius="md"
            fullWidth
            styles={{
              root: {
                backgroundColor: "#124B8C",
                "&:hover": {
                  backgroundColor: "#0f3a6f",
                },
              },
            }}
          >
            New Chat
          </Button>

          <Text size="sm" mt="md">
            Recent Searches
          </Text>

          <Stack gap="xs">
            {recentSearches?.map((search: any) => (
              <Group
                key={search.id}
                justify="space-between"
                p="xs"
                style={{
                  cursor: "pointer",
                  backgroundColor:
                    colorScheme === "dark"
                      ? theme.colors.neutral[8]
                      : theme.colors.neutral[1],
                  borderRadius: theme.radius.sm,
                }}
                onClick={() => {
                  setSearchQuery(search.search_keyword);
                  setSelectedQuestionForRelated(search.search_keyword);
                  setShowSuggestions(false);
                  setExpandedExampleId(null);
                }}
              >
                <Box style={{ flex: 1 }}>
                  <Text size="sm" fw={500} lineClamp={2}>
                    {search.question}
                  </Text>
                </Box>
                <Menu shadow="md" width={120}>
                  <Menu.Target>
                    <ActionIcon 
                      variant="subtle" 
                      size="xs"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DotsThreeVerticalIcon size={16} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteRecentSearchMutation.mutate({ search_id: search.id.toString() }, {
                          onSuccess: () => {
                            refetchRecentSearches();
                          }
                        });
                      }}
                    >
                      Delete
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            ))}
            {isLoadingRecentSearches && (
              <Text size="sm" c="dimmed" ta="center" py="md">
                Loading recent searches...
              </Text>
            )}
            {recentSearchesError && (
              <Text size="sm" c="red" ta="center" py="md">
                Error loading recent searches
              </Text>
            )}
            {!isLoadingRecentSearches && !recentSearchesError && recentSearches.length === 0 && (
              <Text size="sm" c="dimmed" ta="center" py="md">
                No recent searches yet
              </Text>
            )}
          </Stack>
        </Stack>
      </Card>

      {/* Right Panel - Main Chat Interface */}
      <Card withBorder radius="md" p="xl" style={{ flex: 1 }}>
        <Stack>
          {/* Header Section */}
          <Box ta="center" mb="md">
            <Title order={2} mb="xs">
              <Text
                fw={500}
                size="lg"
                component="span"
                gradient={{
                  from: theme.colors.indigo[9],
                  to: theme.colors.negative[3],
                  deg: 90,
                }}
                variant="gradient"
              >
                One Self AI{" "}
              </Text>

              <Text component="span" fw={500} size="lg">
                Your Personal Health Assistant
              </Text>
            </Title>
            <Text size="sm">
              Ask questions, explore care paths, or get insights based on your
              health data
            </Text>
          </Box>

          {/* Input Section with Search Suggestions */}
          <Box style={{ position: "relative", flex: 1 }}>
            <TextInput
              ref={inputRef}
              placeholder="Ask me about your health..."
              size="lg"
              radius="md"
              value={searchQuery}
              onChange={(event) => handleInputChange(event.currentTarget.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setShowSuggestions(searchQuery.length > 0)}
              style={{ flex: 1 }}
              rightSection={
                <ActionIcon
                  size="lg"
                  radius="md"
                  color={
                    colorScheme === "dark"
                      ? theme.colors.indigo[6]
                      : theme.colors.indigo[8]
                  }
                  variant="filled"
                >
                  <PaperPlaneRightIcon size={20} />
                </ActionIcon>
              }
            />

            {/* Search Suggestions Dropdown */}
            {showSuggestions && (
              <Paper
                ref={suggestionsRef}
                shadow="lg"
                radius="md"
                p="xs"
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  maxHeight: 400,
                  overflowY: "auto",
                  border: `1px solid ${
                    colorScheme === "dark"
                      ? theme.colors.neutral[7]
                      : theme.colors.neutral[3]
                  }`,
                  backgroundColor:
                    colorScheme === "dark"
                      ? theme.colors.neutral[9]
                      : theme.colors.neutral[0],
                }}
              >
                <ScrollArea.Autosize mah={400}>
                  <Stack gap="xs">
                    {/* Recent Searches */}
                    {/* {searchQuery.length === 0 && recentSearches.length > 0 && (
                      <>
                        <Group gap="xs" px="sm" py="xs">
                          <ClockIcon
                            size={16}
                            color={theme.colors.neutral[6]}
                          />
                          <Text size="sm" fw={500} c="dimmed">
                            Recent searches
                          </Text>
                        </Group>
                        {recentSearches.map((search: any, index: number) => (
                          <Box
                            key={`recent-${index}`}
                            px="sm"
                            py="xs"
                            style={{
                              cursor: "pointer",
                              borderRadius: theme.radius.sm,
                              backgroundColor:
                                selectedSuggestionIndex === index
                                  ? colorScheme === "dark"
                                    ? theme.colors.neutral[8]
                                    : theme.colors.neutral[1]
                                  : "transparent",
                            }}
                            onClick={() => handleSuggestionClick(search)}
                            onMouseEnter={() =>
                              setSelectedSuggestionIndex(index)
                            }
                          >
                            <Text size="sm">{search}</Text>
                          </Box>
                        ))}
                        <Box px="sm" py="xs">
                          <Text size="xs" c="dimmed" ta="center">
                            Type to search health topics
                          </Text>
                        </Box>
                      </>
                    )} */}

                    {/* Trending Searches
                    {searchQuery.length === 0 &&
                      trendingSearches.length > 0 && (
                        <>
                          <Group gap="xs" px="sm" py="xs">
                            <TrendUpIcon
                              size={16}
                              color={theme.colors.blue[6]}
                            />
                            <Text size="sm" fw={500} c="dimmed">
                              Trending searches
                            </Text>
                          </Group>
                          {trendingSearches.map((search, index) => (
                            <Box
                              key={`trending-${index}`}
                              px="sm"
                              py="xs"
                              style={{
                                cursor: "pointer",
                                borderRadius: theme.radius.sm,
                                backgroundColor:
                                  selectedSuggestionIndex ===
                                  index + recentSearches.length
                                    ? colorScheme === "dark"
                                      ? theme.colors.neutral[8]
                                      : theme.colors.neutral[1]
                                    : "transparent",
                              }}
                              onClick={() => handleSuggestionClick(search)}
                              onMouseEnter={() =>
                                setSelectedSuggestionIndex(
                                  index + recentSearches.length
                                )
                              }
                            >
                              <Text size="sm">{search}</Text>
                            </Box>
                          ))}
                        </>
                      )} */}

                    {/* Search Results */}
                    {debouncedSearchQuery.length > 0 && (
                      <>
                        {isLoadingSuggestions ? (
                          <Group justify="center" py="md">
                            <Loader size="sm" />
                            <Text size="sm" c="dimmed">
                              Searching...
                            </Text>
                          </Group>
                        ) : (questionSuggestions?.length ?? 0) > 0 ? (
                          <>
                            <Group gap="xs" px="sm" py="xs">
                              <MagnifyingGlassIcon
                                size={16}
                                color={theme.colors.neutral[6]}
                              />
                              <Text size="sm" fw={500} c="dimmed">
                                Suggestions
                              </Text>
                            </Group>
                            {questionSuggestions?.map((suggestion, index) => (
                              <Box key={`suggestion-${index}`}>
                                <Box
                                  px="sm"
                                  py="xs"
                                  style={{
                                    cursor: "pointer",
                                    borderRadius: theme.radius.sm,
                                    backgroundColor:
                                      selectedSuggestionIndex === index
                                        ? colorScheme === "dark"
                                          ? theme.colors.neutral[8]
                                          : theme.colors.neutral[1]
                                        : "transparent",
                                  }}
                                  onClick={() =>
                                    handleSuggestionClick(suggestion)
                                  }
                                  onMouseEnter={() =>
                                    setSelectedSuggestionIndex(index)
                                  }
                                >
                                  <Group gap="sm" align="center">
                                    <ClockCounterClockwise 
                                      size={16} 
                                      color={theme.colors.neutral[6]}
                                    />
                                    <Highlight
                                      highlight={searchQuery}
                                      size="sm"
                                      highlightStyles={{
                                        backgroundColor: theme.colors.yellow[2],
                                        color: theme.colors.yellow[9],
                                        fontWeight: 600,
                                      }}
                                      style={{ flex: 1 }}
                                    >
                                      {suggestion?.question}
                                    </Highlight>
                                  </Group>
                                </Box>
                              </Box>
                            ))}
                          </>
                        ) : (
                          <Box px="sm" py="md">
                            <Text size="sm" c="dimmed" ta="center">
                              No suggestions found for "{searchQuery}"
                            </Text>
                          </Box>
                        )}
                      </>
                    )}
                  </Stack>
                </ScrollArea.Autosize>
              </Paper>
            )}
          </Box>

          <Card withBorder radius="md" p="md">
            <Group justify="space-between" align="center" mb="md">
              <Text fw={500} size="lg">
                {selectedQuestionForRelated.length > 0 
                  ? `Questions related to "${selectedQuestionForRelated}"` 
                  : searchQuery.length > 0 
                    ? `Questions related to "${searchQuery}"` 
                    : "Suggested Questions"}
              </Text>
              <Menu shadow="md" width={120}>
                <Menu.Target>
                  <ActionIcon variant="subtle" size="md">
                    <ArrowsDownUpIcon size={20} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item>A-Z</Menu.Item>
                  <Menu.Item>Z-A</Menu.Item>
                  <Menu.Item>Most Recent</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
            <ScrollArea h={250} type="never">
              {exampleQuestions?.length === 0 && debouncedSearchQuery.length > 0 ? (
                <Box p="md" ta="center">
                  <Text size="sm" c="dimmed">
                    No questions found for "{debouncedSearchQuery}"
                  </Text>
                </Box>
              ) : (
                <SimpleGrid cols={2} spacing="xs">
                  {exampleQuestions?.slice(0, 12).map((question) => (
                  <Box key={question.id}>
                    <Card
                      withBorder
                      radius="md"
                      p="md"
                      style={{ cursor: "pointer" }}
                      bg={
                        colorScheme === "dark"
                          ? theme.colors.neutral[8]
                          : theme.colors.neutral[1]
                      }
                      onClick={() => handleExampleQuestionClick(question)}
                    >
                      <Group justify="space-between" align="center">
                        <Group gap="sm">
                          <QuestionIcon size={24} />
                          <Text size="sm" fw={500} lineClamp={2}>
                            {question.question}
                          </Text>
                        </Group>
                        <CaretDownIcon 
                          size={16} 
                          style={{
                            transform: expandedExampleId === question.id ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}
                        />
                      </Group>
                    </Card>
                    
                    {expandedExampleId === question.id && (question as any).explanation && (
                      <Box
                        mt="xs"
                        p="md"
                        style={{
                          backgroundColor: colorScheme === "dark" 
                            ? theme.colors.neutral[7] 
                            : theme.colors.neutral[0],
                          borderLeft: `3px solid ${theme.colors.blue[6]}`,
                          borderRadius: theme.radius.sm,
                        }}
                      >
                        {(question as any).level && (
                          <Text 
                            size="xs" 
                            c={colorScheme === "dark" ? "white" : "dimmed"} 
                            mb="xs"
                          >
                            <strong>Level:</strong> {(question as any).level}
                          </Text>
                        )}
                        <Text size="sm" style={{ lineHeight: 1.5 }}>
                          {(question as any).explanation}
                        </Text>
                      </Box>
                    )}
                  </Box>
                  ))}
                </SimpleGrid>
              )}
            </ScrollArea>
          </Card>
        </Stack>
      </Card>
    </Group>
  );
}
